import { X } from 'lucide-react';
import { Dialog as RadixDialog } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { IconButton } from './button';

export const Dialog = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;

export interface DialogContentProps {
  title: ReactNode;
  description?: ReactNode;
  /** Accessible label for the close button (translated by the caller). */
  closeLabel: string;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  /** For decisions that must be made (e.g. a sync conflict): no close button. */
  hideClose?: boolean;
}

export function DialogContent({
  title,
  description,
  closeLabel,
  children,
  footer,
  className,
  hideClose = false,
}: DialogContentProps) {
  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className="fixed inset-0 z-40 animate-fade-in bg-overlay" />
      <RadixDialog.Content
        className={cn(
          'fixed top-[18vh] left-1/2 z-50 flex max-h-[70vh] w-[min(560px,calc(100vw-32px))] -translate-x-1/2 animate-scale-in flex-col',
          'rounded-lg bg-bg-2 text-text-1 shadow-popover outline-none',
          className,
        )}
        {...(description === undefined ? { 'aria-describedby': undefined } : {})}
      >
        <header className="flex items-start justify-between gap-4 px-4 pt-3.5 pb-2">
          <div className="min-w-0">
            <RadixDialog.Title className="text-base font-medium">{title}</RadixDialog.Title>
            {description !== undefined && (
              <RadixDialog.Description className="mt-0.5 text-sm text-text-3">
                {description}
              </RadixDialog.Description>
            )}
          </div>
          {!hideClose && (
            <RadixDialog.Close asChild>
              <IconButton label={closeLabel} size="sm">
                <X />
              </IconButton>
            </RadixDialog.Close>
          )}
        </header>
        {children !== undefined && <div className="overflow-y-auto px-4 pb-4">{children}</div>}
        {footer !== undefined && (
          <footer className="flex justify-end gap-2 border-t border-border-subtle px-4 py-2.5">
            {footer}
          </footer>
        )}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
}
